(() => {
  'use strict';

  const doc = document;
  const body = doc.body;
  const $ = (selector, context = doc) => context.querySelector(selector);
  const $$ = (selector, context = doc) => Array.from(context.querySelectorAll(selector));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const animationsEnabled = body.dataset.enableAnimations === 'true' && !reducedMotion;
  const storeMode = body.dataset.storeMode || 'catalog_only';
  const routes = {
    cartAdd: body.dataset.cartAddUrl,
    cartChange: body.dataset.cartChangeUrl,
    cart: body.dataset.cartUrl,
    predictiveSearch: body.dataset.predictiveSearchUrl
  };
  const messages = {
    added: body.dataset.addedMessage || 'Added to cart',
    cartError: body.dataset.cartErrorMessage || 'Something went wrong',
    noResults: body.dataset.searchEmptyMessage || 'No results'
  };
  const currency = body.dataset.currency || 'TRY';
  let lastFocusedElement = null;
  let searchController = null;

  const escapeHTML = (value = '') => String(value).replace(/[&<>'"]/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[char]));

  const cssEscape = value => (window.CSS && CSS.escape ? CSS.escape(String(value)) : String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&'));

  const safeURL = (value = '') => {
    try {
      const url = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '#';
    } catch (_) {
      return '#';
    }
  };

  const localizedStorefrontURL = (value = '') => {
    const safe = safeURL(value);
    try {
      const url = new URL(safe);
      const locale = location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/i)?.[1];
      const storefrontPath = /^\/(products|collections|pages|policies|cart)(?:\/|$)/.test(url.pathname);
      if (locale && url.origin === location.origin && storefrontPath) url.pathname = `/${locale}${url.pathname}`;
      return url.href;
    } catch (_) {
      return safe;
    }
  };

  const initLocalizedLinks = (context = doc) => {
    $$('a[href]', context).forEach(link => {
      if (!['http:', 'https:'].includes(link.protocol)) return;
      const localized = localizedStorefrontURL(link.href);
      if (localized !== link.href) link.href = localized;
    });
  };

  const money = cents => new Intl.NumberFormat(doc.documentElement.lang || 'tr-TR', {
    style: 'currency', currency
  }).format(Number(cents || 0) / 100);
  const setMoney = (element, cents) => {
    if (!element) return;
    if (window.MivekaMoney) window.MivekaMoney.set(element, cents);
    else element.textContent = money(cents);
  };
  const storedPriceCents = item => {
    if (item?.priceCents !== undefined && item.priceCents !== null && Number.isFinite(Number(item.priceCents))) return Number(item.priceCents);
    return window.MivekaMoney?.parseTRY(item?.price) ?? null;
  };
  const priceMarkup = (cents, fallback = '') => Number.isFinite(cents)
    ? `data-miveka-money data-money-cents="${cents}">${escapeHTML(window.MivekaMoney ? window.MivekaMoney.format(cents) : money(cents))}`
    : `>${escapeHTML(fallback)}`;
  const normalizeStoredPrice = value => {
    const text = String(value || '').trim();
    const match = text.match(/^([\d.,]+)\s*(?:TRY|TL)$/i);
    if (!match) return text;
    const numeric = match[1];
    const decimal = numeric.includes('.') ? numeric.split('.').pop() : '00';
    const lira = numeric.split('.')[0].replace(/[^\d]/g, '');
    if (!lira) return text;
    return `₺${lira.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimal.padEnd(2, '0').slice(0, 2)}`;
  };

  const toast = message => {
    const element = $('[data-toast]');
    if (!element) return;
    element.textContent = message || messages.added;
    element.classList.add('is-visible');
    window.clearTimeout(element._timer);
    element._timer = window.setTimeout(() => element.classList.remove('is-visible'), 1800);
  };

  const fetchJSON = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(options.headers || {}) },
      ...options
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.description || payload.message || messages.cartError);
    return payload;
  };

  const focusable = container => $$('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', container).filter(el => !el.hidden && el.offsetParent !== null);

  const trapFocus = (event, container) => {
    if (event.key !== 'Tab') return;
    const items = focusable(container);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && doc.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && doc.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const openSurface = (surface, backdrop) => {
    if (!surface) return;
    lastFocusedElement = doc.activeElement;
    surface.classList.add('is-open');
    surface.setAttribute('aria-hidden', 'false');
    backdrop?.classList.add('is-open');
    body.classList.add('mv-overflow-hidden');
    window.setTimeout(() => focusable(surface)[0]?.focus(), 30);
  };

  const closeSurface = (surface, backdrop) => {
    if (!surface) return;
    surface.classList.remove('is-open');
    surface.setAttribute('aria-hidden', 'true');
    backdrop?.classList.remove('is-open');
    body.classList.remove('mv-overflow-hidden');
    lastFocusedElement?.focus?.();
  };

  const initMotion = () => {
    if (!animationsEnabled) return;
    const cursor = $('[data-cursor]');
    const transition = $('[data-transition]');
    doc.addEventListener('pointermove', event => {
      if (cursor && event.pointerType !== 'touch') {
        cursor.style.left = `${event.clientX}px`;
        cursor.style.top = `${event.clientY}px`;
      }
      doc.documentElement.style.setProperty('--mx', (event.clientX / innerWidth - 0.5).toFixed(3));
      doc.documentElement.style.setProperty('--my', (event.clientY / innerHeight - 0.5).toFixed(3));
    }, { passive: true });
    doc.addEventListener('click', event => {
      const link = event.target.closest('a[href]');
      if (!link || link.target === '_blank' || link.hasAttribute('download') || link.origin !== location.origin || (link.hash && link.pathname === location.pathname)) return;
      transition?.classList.remove('is-playing');
      if (transition) void transition.offsetWidth;
      transition?.classList.add('is-playing');
    });
  };

  const initReveal = (context = doc) => {
    const items = $$('.mv-reveal:not(.is-observed)', context);
    if (!items.length) return;
    if (!animationsEnabled || !('IntersectionObserver' in window)) {
      items.forEach(item => item.classList.add('is-visible', 'is-observed'));
      return;
    }
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    }), { rootMargin: '0px 0px -6% 0px', threshold: 0.08 });
    items.forEach(item => {
      item.classList.add('is-observed');
      observer.observe(item);
    });
  };

  const initHeader = () => {
    const mobileMenu = $('[data-mobile-menu]');
    const mobileToggle = $('[data-mobile-toggle]');
    const mobileClose = $('[data-mobile-close]');
    const mobileBackdrop = $('[data-mobile-backdrop]');
    const closeMobileMenu = () => {
      closeSurface(mobileMenu, mobileBackdrop);
      mobileToggle?.setAttribute('aria-expanded', 'false');
      mobileToggle?.setAttribute('aria-label', body.dataset.menuOpenMessage || 'Menu');
    };
    const openMobileMenu = () => {
      openSurface(mobileMenu, mobileBackdrop);
      mobileToggle?.setAttribute('aria-expanded', 'true');
      mobileToggle?.setAttribute('aria-label', body.dataset.menuCloseMessage || 'Close');
    };
    mobileToggle?.addEventListener('click', () => mobileMenu?.classList.contains('is-open') ? closeMobileMenu() : openMobileMenu());
    mobileClose?.addEventListener('click', closeMobileMenu);
    mobileBackdrop?.addEventListener('click', closeMobileMenu);
    $$('a[href]', mobileMenu).forEach(link => link.addEventListener('click', closeMobileMenu));

    const marketToggle = $('[data-market-toggle]');
    const marketPanel = $('[data-market-panel]');
    marketToggle?.addEventListener('click', () => {
      const open = marketPanel?.hasAttribute('hidden');
      if (open) marketPanel.removeAttribute('hidden');
      else marketPanel?.setAttribute('hidden', '');
      marketToggle.setAttribute('aria-expanded', String(open));
    });
    doc.addEventListener('click', event => {
      if (!event.target.closest('[data-market-switcher]') && marketPanel && !marketPanel.hasAttribute('hidden')) {
        marketPanel.setAttribute('hidden', '');
        marketToggle?.setAttribute('aria-expanded', 'false');
      }
    });
    $$('[data-localization-select]').forEach(select => select.addEventListener('change', () => select.form?.submit()));

    window.MivekaMoney?.refresh();
  };

  const initNightSystem = (context = doc) => {
    $$('[data-night-system]', context).forEach(root => {
      const tabs = $$('[data-night-tab]', root);
      const panels = $$('[data-night-panel]', root);
      const activate = tab => {
        tabs.forEach(item => item.setAttribute('aria-selected', String(item === tab)));
        panels.forEach(panel => { panel.hidden = panel.dataset.nightPanel !== tab.dataset.nightTab; });
      };
      tabs.forEach((tab, index) => {
        if (tab.dataset.mvReady === 'true') return;
        tab.dataset.mvReady = 'true';
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
          event.preventDefault();
          const direction = ['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1;
          const next = tabs[(index + direction + tabs.length) % tabs.length];
          next.focus();
          activate(next);
        });
      });
    });
  };

  const cartDrawer = $('[data-cart-drawer]');
  const cartBackdrop = $('[data-cart-backdrop]');

  // Cart interactions are handled in assets/cart-drawer.js.
  const initCart = () => {};

  const wishlistDrawer = $('[data-wishlist-drawer]');
  const wishlistBackdrop = $('[data-wishlist-backdrop]');
  const wishlistKey = 'mv-wishlist-v2';
  const localizedStoredProductTitle = item => {
    const title = String(item.title || '');
    if (!/^en(?:-|$)/i.test(doc.documentElement.lang)) return title;
    return title
      .replace(/reflekt[iİıI]f/gi, 'Reflective')
      .replace(/boyunluk/gi, 'Neck Gaiter')
      .replace(/tayt/gi, 'Leggings')
      .replace(/çorap/gi, 'Socks')
      .replace(/anahtarl[ıiİI]k/gi, 'Keychain')
      .replace(/kask sticker set[iİıI]/gi, 'Helmet Sticker Set');
  };
  const getWishlist = () => {
    try { return JSON.parse(localStorage.getItem(wishlistKey) || '[]'); }
    catch (_) { return []; }
  };
  const setWishlist = list => localStorage.setItem(wishlistKey, JSON.stringify(list.slice(0, 100)));

  const renderWishlist = () => {
    const list = getWishlist();
    $$('[data-wishlist-count]').forEach(element => { element.textContent = list.length; });
    $$('[data-wishlist]').forEach(button => {
      const active = list.some(item => String(item.id) === String(button.dataset.productId));
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    const wrap = $('[data-wishlist-items]');
    if (!wrap) return;
    if (!list.length) {
      wrap.innerHTML = `<p>${escapeHTML(window.MivekaI18n ? window.MivekaI18n.wishlistEmpty : 'Your wishlist is empty.')}</p>`;
      return;
    }
    wrap.innerHTML = list.map(item => `<article class="mv-wishlist-line">${item.image ? `<img src="${escapeHTML(safeURL(item.image))}" alt="" width="80" height="100" loading="lazy">` : ''}<div><a href="${escapeHTML(localizedStorefrontURL(item.url))}">${escapeHTML(localizedStoredProductTitle(item))}</a><small ${priceMarkup(storedPriceCents(item), item.price || '')}</small><button type="button" data-wishlist-remove="${escapeHTML(item.id)}">${window.MivekaI18n ? window.MivekaI18n.remove : 'Remove'}</button></div></article>`).join('');
    $$('[data-wishlist-remove]', wrap).forEach(button => button.addEventListener('click', () => {
      setWishlist(getWishlist().filter(item => String(item.id) !== String(button.dataset.wishlistRemove)));
      renderWishlist();
    }));
  };

  const initWishlist = () => {
    $('[data-wishlist-open]')?.addEventListener('click', () => { renderWishlist(); openSurface(wishlistDrawer, wishlistBackdrop); });
    $('[data-wishlist-close]')?.addEventListener('click', () => closeSurface(wishlistDrawer, wishlistBackdrop));
    wishlistBackdrop?.addEventListener('click', () => closeSurface(wishlistDrawer, wishlistBackdrop));
    doc.addEventListener('click', event => {
      const button = event.target.closest('[data-wishlist]');
      if (!button) return;
      let list = getWishlist();
      const id = String(button.dataset.productId);
      if (list.some(item => String(item.id) === id)) {
        list = list.filter(item => String(item.id) !== id);
      } else {
        list.push({
          id,
          title: button.dataset.productTitle || '',
          url: button.dataset.productUrl || '#',
          image: button.dataset.productImage || '',
          price: button.dataset.productPrice || '',
          priceCents: button.dataset.productPriceCents ? Number(button.dataset.productPriceCents) : window.MivekaMoney?.parseTRY(button.dataset.productPrice),
          sourceCurrency: 'TRY'
        });
      }
      setWishlist(list);
      renderWishlist();
    });
    renderWishlist();
  };

  const searchOverlay = $('[data-search-overlay]');
  const searchInput = $('[data-search-input]', searchOverlay);
  const searchResults = $('[data-search-results]', searchOverlay);

  const closeSearch = () => closeSurface(searchOverlay);
  const renderSearchResults = products => {
    if (!searchResults) return;
    if (!products.length) {
      searchResults.innerHTML = `<p class="mv-search-empty">${escapeHTML(messages.noResults)}</p>`;
      return;
    }
    searchResults.innerHTML = products.map(product => {
      const imageSource = product.image?.url || product.image || '';
      const image = imageSource ? `<img src="${escapeHTML(safeURL(imageSource))}" alt="" width="72" height="90" loading="lazy">` : '';
      const searchPrice = Number(product.price);
      const cents = product.price !== undefined && product.price !== null && String(product.price).trim() !== '' && Number.isFinite(searchPrice) ? Math.round(searchPrice * 100) : window.MivekaMoney?.parseTRY(product.price);
      return `<a class="mv-search-result" href="${escapeHTML(safeURL(product.url))}">${image}<span>${escapeHTML(product.title)}</span><strong ${priceMarkup(cents, product.price || '')}</strong></a>`;
    }).join('');
  };

  const runPredictiveSearch = async query => {
    if (!routes.predictiveSearch || query.length < 2) {
      if (searchResults) searchResults.innerHTML = '';
      return;
    }
    searchController?.abort();
    searchController = new AbortController();
    const base = routes.predictiveSearch.endsWith('.json') ? routes.predictiveSearch : `${routes.predictiveSearch}.json`;
    const url = new URL(base, location.origin);
    url.searchParams.set('q', query.slice(0, 100));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    try {
      const response = await fetch(url, { credentials: 'same-origin', signal: searchController.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(messages.noResults);
      const data = await response.json();
      renderSearchResults(data.resources?.results?.products || []);
    } catch (error) {
      if (error.name !== 'AbortError') renderSearchResults([]);
    }
  };

  const initSearch = () => {
    $('[data-search-open]')?.addEventListener('click', () => openSurface(searchOverlay));
    $('[data-search-close]')?.addEventListener('click', closeSearch);
    searchOverlay?.addEventListener('click', event => { if (event.target === searchOverlay) closeSearch(); });
    let timer;
    searchInput?.addEventListener('input', event => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => runPredictiveSearch(event.target.value.trim()), 220);
    });
  };

  const parseProductData = root => {
    try { return JSON.parse($('[data-product-json]', root)?.textContent || '{}'); }
    catch (_) { return {}; }
  };

  const selectVariant = root => {
    const data = parseProductData(root);
    if (!Array.isArray(data.variants)) return;
    const selectedOptions = (data.options || []).map((_, index) => $(`input[name="option-${index + 1}"]:checked`, root)?.value);

    const normalizeVariantToken = value => String(value || '')
      .toLocaleLowerCase('tr-TR')
      .replace(/[ı]/g, 'i')
      .replace(/[ş]/g, 's')
      .replace(/[ğ]/g, 'g')
      .replace(/[ü]/g, 'u')
      .replace(/[ö]/g, 'o')
      .replace(/[ç]/g, 'c');

    const filterProductMedia = () => {
      const optionNames = (data.options || []).map(normalizeVariantToken);
      const colorOptionIndex = optionNames.findIndex(opt => opt === 'renk' || opt === 'color' || opt === 'colour');
      const printOptionIndex = optionNames.findIndex(opt => opt.includes('dtf') || (opt.includes('baski') && opt.includes('ren')) || opt.includes('print color'));
      const productHandle = root.dataset.productHandle || '';
      const isNightshift = productHandle.includes('nightshift-');
      if (!isNightshift && (colorOptionIndex === -1 || !selectedOptions[colorOptionIndex])) return false;

      const slides = $$('.mv-product__slide', root);
      const variantSlides = slides.filter(slide => slide.dataset.variantMedia !== undefined);
      if (!variantSlides.length) return false;

      const selectedColor = colorOptionIndex !== -1 ? normalizeVariantToken(selectedOptions[colorOptionIndex]) : '';
      const selectedPrintRaw = printOptionIndex !== -1 ? normalizeVariantToken(selectedOptions[printOptionIndex]) : '';
      const selectedPrint = selectedPrintRaw === 'gri' ? 'siyah' : selectedPrintRaw;
      if (isNightshift && selectedPrint) {
        const printMatches = variantSlides.filter(slide => normalizeVariantToken(slide.dataset.mediaPrint) === selectedPrint);
        if (printMatches.length) {
          slides.forEach(slide => { slide.hidden = !printMatches.includes(slide); });
          const slider = $('[data-product-slider]', root);
          const firstVisible = printMatches[0];
          if (slider && firstVisible) {
            slider.scrollTo({ left: firstVisible.offsetLeft - slider.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
          }
          return true;
        }
      }
      const colorMatches = variantSlides.filter(slide => normalizeVariantToken(slide.dataset.mediaColor) === selectedColor);
      const exactMatches = colorMatches.filter(slide => {
        if (!selectedPrint || slide.dataset.mediaCommon === 'true') return true;
        const mediaPrint = normalizeVariantToken(slide.dataset.mediaPrint);
        return !mediaPrint || mediaPrint === selectedPrint;
      });
      const visibleSlides = exactMatches.length ? exactMatches : (colorMatches.length ? colorMatches : variantSlides);

      slides.forEach(slide => { slide.hidden = !visibleSlides.includes(slide); });

      const slider = $('[data-product-slider]', root);
      const firstVisible = visibleSlides[0];
      if (slider && firstVisible) {
        slider.scrollTo({ left: firstVisible.offsetLeft - slider.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
      }
      return true;
    };
    
    (data.options || []).forEach((_, optIndex) => {
      $$(`input[name="option-${optIndex + 1}"]`, root).forEach(input => {
        const testOptions = [...selectedOptions];
        testOptions[optIndex] = input.value;
        const exactVariant = data.variants.find(item => item.options.every((opt, idx) => opt === testOptions[idx]));
        const label = input.closest('label');
        if (label) label.classList.toggle('is-sold-out', exactVariant ? !exactVariant.available : true);
      });
    });

    const mediaFilteringActive = filterProductMedia();

    const variant = data.variants.find(item => item.options.every((option, index) => option === selectedOptions[index]));
    if (!variant) return;
    const idInput = $('[data-variant-id]', root);
    const price = $('[data-product-price]', root);
    const comparePrice = $('[data-compare-price]', root);
    const quantity = $('[data-quantity]', root);
    const addButton = $('[data-add-button]', root);
    const stockMessage = $('[data-stock-message]', root);
    const sku = $('[data-product-sku]', root);
    const skuRow = sku?.closest('.mv-product__sku');
    const stickyVariant = $('[data-sticky-variant]');
    const stickyPrice = $('[data-sticky-price]');

    if (idInput) idInput.value = variant.id;
    setMoney(price, variant.price);
    if (comparePrice) {
      comparePrice.hidden = !(variant.compare_at_price > variant.price);
      setMoney(comparePrice, variant.compare_at_price || 0);
    }
    if (sku) sku.textContent = variant.sku || '';
    if (skuRow) skuRow.hidden = !variant.sku;
    if (quantity) {
      quantity.value = Math.max(1, Math.min(Number(quantity.value) || 1, Math.max(1, variant.inventory_quantity || 1)));
      if (variant.inventory_management && variant.inventory_policy === 'deny') quantity.max = Math.max(0, variant.inventory_quantity);
      else quantity.removeAttribute('max');
    }
    if (addButton) {
      addButton.disabled = !variant.available;
      addButton.textContent = variant.available ? addButton.dataset.addLabel : addButton.dataset.soldLabel;
    }
    
    // Update URL with selected variant
    window.history.replaceState({}, '', `${window.location.pathname}?variant=${variant.id}`);

    // Scroll to variant image
    if (!mediaFilteringActive && variant.featured_media_id) {
      const slider = $('[data-product-slider]', root);
      const slide = $(`[data-media-id="${variant.featured_media_id}"]`, root);
      if (slider && slide) {
        const scrollTarget = slide.offsetLeft - slider.offsetLeft;
        slider.scrollTo({ left: scrollTarget, behavior: 'smooth' });
      }
    }
    
    const notifyForm = $('[data-notify-form]', root);
    const notifyVariant = $('[data-notify-variant-name]', root);
    if (notifyForm) notifyForm.hidden = variant.available;
    if (notifyVariant) notifyVariant.value = variant.title;
    
    if (stockMessage) {
      const threshold = Number(root.dataset.lowStockThreshold || 10);
      const show = variant.available && variant.inventory_management && variant.inventory_policy === 'deny' && variant.inventory_quantity > 0 && variant.inventory_quantity <= threshold;
      stockMessage.hidden = !show;
      if (show) stockMessage.textContent = window.MivekaI18n ? window.MivekaI18n.stockLeft.replace('{{ count }}', variant.inventory_quantity) : `Only ${variant.inventory_quantity} left`;
    }
    if (stickyVariant) stickyVariant.value = String(variant.id);
    setMoney(stickyPrice, variant.price);
    if (!mediaFilteringActive && variant.featured_media_id) $(`[data-media-id="${variant.featured_media_id}"]`, root)?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'nearest' });
    const url = new URL(location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url);
  };

  const initProduct = () => {
    const root = $('[data-product-root]');
    if (!root) return;
    root.dataset.lowStockThreshold = body.dataset.lowStockThreshold || '10';
    root.addEventListener('change', event => {
      if (event.target.matches('input[type="radio"][name^="option-"]')) selectVariant(root);
    });
    // Initialize visually disabled variants on load
    selectVariant(root);
    
    $('[data-sticky-variant]')?.addEventListener('change', event => {
      const id = Number(event.target.value);
      const data = parseProductData(root);
      const variant = data.variants?.find(item => item.id === id);
      if (!variant) return;
      variant.options.forEach((value, index) => {
        const option = $(`input[name="option-${index + 1}"][value="${cssEscape(value)}"]`, root);
        if (option) option.checked = true;
      });
      selectVariant(root);
    });

    // Quantity selector logic
    const qtyInput = $('input[data-quantity]', root) || $('input[data-catalog-quantity]', root);
    const qtyMinus = $('[data-quantity-minus]', root);
    const qtyPlus = $('[data-quantity-plus]', root);
    
    if (qtyInput && qtyMinus && qtyPlus) {
      qtyMinus.addEventListener('click', () => {
        const min = Number(qtyInput.min) || 1;
        const current = Number(qtyInput.value) || 1;
        if (current > min) qtyInput.value = current - 1;
      });
      qtyPlus.addEventListener('click', () => {
        const max = Number(qtyInput.max) || Infinity;
        const current = Number(qtyInput.value) || 1;
        if (current < max) qtyInput.value = current + 1;
      });
    }

    const modal = $('[data-size-guide-modal]');
    $('[data-size-guide-open]')?.addEventListener('click', () => openSurface(modal));
    $$('[data-size-guide-close]').forEach(button => button.addEventListener('click', () => closeSurface(modal)));
  };

  const initFilters = () => {
    $('[data-sort-select]')?.addEventListener('change', event => event.target.form?.submit());
    const panel = $('[data-facets-panel]');
    const toggle = $('[data-filter-toggle]');
    toggle?.addEventListener('click', () => {
      const open = panel?.classList.toggle('is-open') || false;
      toggle.setAttribute('aria-expanded', String(open));
      panel?.setAttribute('aria-hidden', String(!open));
    });
  };

  const initSecureForms = () => {
    doc.addEventListener('submit', event => {
      const form = event.target.closest('[data-secure-form]');
      if (!form) return;
      const honeypot = $('[data-honeypot]', form);
      if (honeypot?.value) {
        event.preventDefault();
        form.reset();
      }
    }, true);
  };

  const initCountdowns = () => {
    $$('[data-countdown]').forEach(countdown => {
      const target = Date.parse(countdown.dataset.countdownAt || '');
      const output = $('[data-countdown-output]', countdown);
      if (!Number.isFinite(target) || !output) {
        countdown.hidden = true;
        return;
      }
      const isTR = doc.documentElement.lang.startsWith('tr');
      const labels = isTR ? ['gün', 'saat', 'dk', 'sn'] : ['days', 'hours', 'min', 'sec'];
      const update = () => {
        const remaining = target - Date.now();
        if (remaining <= 0) {
          output.textContent = countdown.dataset.countdownExpired || (window.MivekaI18n ? window.MivekaI18n.timeEnded : 'Time ended');
          window.clearInterval(countdown._timer);
          return;
        }
        const seconds = Math.floor(remaining / 1000);
        const values = [Math.floor(seconds / 86400), Math.floor((seconds % 86400) / 3600), Math.floor((seconds % 3600) / 60), seconds % 60];
        output.innerHTML = values.map((value, index) => `<span><b>${String(value).padStart(2, '0')}</b><small>${labels[index]}</small></span>`).join('');
      };
      update();
      countdown._timer = window.setInterval(update, 1000);
    });
  };

  const initRecentlyViewed = () => {
    const root = $('[data-recently-viewed]');
    if (!root) return;
    const normalizeProductTitle = item => {
      const url = String(item?.url || '').toLowerCase();
      if (url.includes('/mvk-v1-reflektif-oversize-t-shirt')) return 'MVK-V1 - REFLEKTİF OVERSIZE T-SHIRT';
      if (url.includes('/mvk-v2-reflektif-oversize-t-shirt')) return 'MVK-V2 - REFLEKTİF OVERSIZE T-SHIRT';
      if (url.includes('/mvk-v3-reflektif-oversize-t-shirt')) return 'MVK-V3 - REFLEKTİF OVERSIZE T-SHIRT';
      if (url.includes('/mvk-v4-reflektif-oversize-t-shirt')) return 'MVK-V4 - REFLEKTİF OVERSIZE T-SHIRT';
      return item?.title || '';
    };
    let current;
    try { current = JSON.parse($('[data-current-product-json]', root)?.textContent || '{}'); }
    catch (_) { current = null; }
    if (!current?.id) return;
    current.title = normalizeProductTitle(current);
    const key = 'mv-recently-viewed-v1';
    let list = [];
    try { list = JSON.parse(localStorage.getItem(key) || '[]'); }
    catch (_) { list = []; }
    current.priceCents = storedPriceCents(current);
    if (current.priceCents !== null) current.price = money(current.priceCents);
    current.sourceCurrency = 'TRY';
    const previous = list.filter(item => String(item.id) !== String(current.id)).slice(0, 3).map(item => ({ ...item, title: normalizeProductTitle(item), priceCents: storedPriceCents(item) }));
    const wrap = $('[data-recently-viewed-list]', root);
    if (!previous.length) root.hidden = true;
    else if (wrap) wrap.innerHTML = previous.map(item => `<a class="mv-recent-card" href="${escapeHTML(localizedStorefrontURL(item.url))}">${item.image ? `<img src="${escapeHTML(safeURL(item.image))}" alt="" width="180" height="220" loading="lazy">` : '<span class="mv-recent-card__placeholder">MIVEKA</span>'}<span><strong>${escapeHTML(localizedStoredProductTitle(item))}</strong><small ${priceMarkup(storedPriceCents(item), item.price || '')}</small></span></a>`).join('');
    try { localStorage.setItem(key, JSON.stringify([current, ...previous].slice(0, 4))); }
    catch (_) { /* Storage can be unavailable in private browsing. */ }
  };

  const initProductRecommendations = async () => {
    const root = $('[data-product-recommendations]');
    if (!root || !root.dataset.url || root.dataset.loaded === 'true') return;
    root.dataset.loaded = 'true';
    try {
      const response = await fetch(root.dataset.url, { credentials: 'same-origin' });
      if (!response.ok) return;
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const content = $('[data-product-recommendations]', parsed);
      if (content?.innerHTML.trim()) {
        root.innerHTML = content.innerHTML;
        initReveal(root);
      }
    } catch (_) { /* Recommendations are optional and fail silently. */ }
  };

  const initScroll = () => {
    // Scroll progress bar
    const progress = doc.createElement('div');
    progress.className = 'mv-scroll-progress';
    body.appendChild(progress);

    let ticking = false;
    const update = () => {
      const scrollTop = window.scrollY;
      const docHeight = doc.documentElement.scrollHeight - window.innerHeight;
      const pct = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      progress.style.width = `${pct.toFixed(1)}%`;
      doc.documentElement.style.setProperty('--scroll', String(scrollTop));
      $('[data-sticky-atc]')?.classList.toggle('is-visible', scrollTop > 650);
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
    update();
  };

  const initPremiumAnimations = () => {
    if (!animationsEnabled) {
      // Ensure everything is visible if animations are off
      $$('.mv-section, .mv-product__info').forEach(el => el.classList.add('is-visible'));
      $$('.mv-product-grid .mv-product-card').forEach(el => el.classList.add('is-visible'));
      return;
    }

    // Section entrance observer
    const sectionObserver = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        sectionObserver.unobserve(entry.target);
      }
    }), { threshold: 0.06 });
    $$('.mv-section:not(.is-visible)').forEach(el => sectionObserver.observe(el));

    // Product info stagger observer
    const infoObserver = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        infoObserver.unobserve(entry.target);
      }
    }), { threshold: 0.1 });
    $$('.mv-product__info').forEach(el => infoObserver.observe(el));

    // Staggered product card reveal
    const cardObserver = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        cardObserver.unobserve(entry.target);
      }
    }), { rootMargin: '0px 0px -5% 0px', threshold: 0.05 });
    $$('.mv-product-grid .mv-product-card').forEach(el => cardObserver.observe(el));

    // 3D tilt on product cards (desktop only)
    if (window.matchMedia('(hover: hover)').matches) {
      $$('.mv-product-card').forEach(card => {
        card.addEventListener('mousemove', e => {
          const rect = card.getBoundingClientRect();
          const x = (e.clientX - rect.left) / rect.width;
          const y = (e.clientY - rect.top) / rect.height;
          const rotX = (y - 0.5) * -10;
          const rotY = (x - 0.5) * 10;
          card.style.transform = `translateY(-10px) rotateX(${rotX}deg) rotateY(${rotY}deg) scale(1.02)`;
          card.style.setProperty('--mx-local', `${(x * 100).toFixed(1)}%`);
          card.style.setProperty('--my-local', `${(y * 100).toFixed(1)}%`);
        }, { passive: true });
        card.addEventListener('mouseleave', () => {
          card.style.transform = '';
          card.style.removeProperty('--mx-local');
          card.style.removeProperty('--my-local');
        });
      });
    }
  };

  doc.addEventListener('keydown', event => {
    const openDrawer = $('.mv-drawer.is-open');
    const openMobileMenu = $('.mv-mobile-menu.is-open');
    const openSearch = $('.mv-search-overlay.is-open');
    const openModal = $('.mv-modal.is-open');
    if (openDrawer === cartDrawer) return;
    const activeSurface = openDrawer || openMobileMenu || openSearch || openModal;
    if (event.key === 'Escape') {
      if (openDrawer === wishlistDrawer) closeSurface(wishlistDrawer, wishlistBackdrop);
      else if (openMobileMenu) {
        closeSurface(openMobileMenu, $('[data-mobile-backdrop]'));
        $('[data-mobile-toggle]')?.setAttribute('aria-expanded', 'false');
      }
      else if (openSearch) closeSearch();
      else if (openModal) closeSurface(openModal);
    }
    if (activeSurface) trapFocus(event, activeSurface);
  });

  initLocalizedLinks();
  initMotion();
  initReveal();
  initHeader();
  initNightSystem();
  initCart();
  initWishlist();
  initSearch();
  initProduct();
  initFilters();
  initSecureForms();
  initCountdowns();
  initRecentlyViewed();
  initProductRecommendations();
  initScroll();
  initPremiumAnimations();

  doc.addEventListener('shopify:section:load', event => {
    initLocalizedLinks(event.target);
    initReveal(event.target);
    initCountdowns();
    initNightSystem(event.target);
  });
})();

